import { registrationClient } from '../config/api';
import {
  ApiResponse,
  PropertyType,
  CreatePropertyTypeRequest,
  UpdatePropertyTypeRequest,
  SearchPropertyTypesParams,
} from './types/user.types';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';

export class PropertyTypeService {
  private readonly basePath = '/api/property-types';

  createPropertyType = async (data: CreatePropertyTypeRequest): Promise<PropertyType | string> => {
    try {
      const response = await registrationClient.post<ApiResponse<PropertyType | string> | PropertyType | string>(this.basePath, data);

      // Suporta resposta direta ou com wrapper
      if (typeof response.data === 'string' || (response.data && 'id' in response.data && 'name' in response.data)) {
        return response.data as PropertyType | string;
      }

      if (response.data?.success === false) {
        throw new Error(response.data?.errorMessage || 'Error creating property type');
      }

      return (response.data?.data || response.data) as PropertyType | string;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error creating property type');
      throw error;
    }
  }

  getAllPropertyTypes = async (): Promise<PropertyType[]> => {
    try {
      const response = await registrationClient.get<ApiResponse<PropertyType[]> | PropertyType[]>(this.basePath);

      // Suporta tanto formato com wrapper quanto direto
      if (Array.isArray(response.data)) {
        return response.data;
      }

      if (response.data?.success === false) {
        throw new Error(response.data?.errorMessage || 'Error fetching property types');
      }

      return response.data?.data || [];
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching property types');
    }
  }

  getPropertyTypeById = async (id: string): Promise<PropertyType> => {
    try {
      const response = await registrationClient.get<ApiResponse<PropertyType>>(`${this.basePath}/${id}`);

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error fetching property type');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching property type');
    }
  }

  updatePropertyType = async (id: string, data: UpdatePropertyTypeRequest): Promise<void> => {
    try {
      const response = await registrationClient.put<ApiResponse<void> | void>(
        `${this.basePath}/${id}`,
        data
      );

      // Só lança erro se explicitamente success = false
      if (response.data && typeof response.data === 'object' && 'success' in response.data && response.data.success === false) {
        throw new Error((response.data as ApiResponse<void>)?.errorMessage || 'Error updating property type');
      }
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error updating property type');
    }
  }

  deletePropertyType = async (id: string): Promise<void> => {
    try {
      const response = await registrationClient.delete<ApiResponse<void>>(`${this.basePath}/${id}`);

      if (response.data && !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error deleting property type');
      }
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error deleting property type');
    }
  }

  searchPropertyTypes = async (params: SearchPropertyTypesParams): Promise<PropertyType[]> => {
    try {
      const response = await registrationClient.get<ApiResponse<PropertyType[]>>(`${this.basePath}/search`, {
        params,
      });

      if (!response.data || !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error searching property types');
      }

      return response.data.data || [];
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error searching property types');
    }
  }
}

export const propertyTypeService = new PropertyTypeService();
